import {
  BaseMutationOptions,
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import type { ApolloError } from "@apollo/client/core"

import { callMutation } from "shared/api/callMutation"
import {
  DuplicateEntityDocument,
  DuplicateEntityMutation,
  DuplicateEntityMutationVariables
} from "shared/api/duplicate/mutations.generated"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { useProcess } from "shared/api/process/useProcess"
import { MutationSchema } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProjectTypename, ShareLinkTypename } from "shared/types/Project"
import {
  CREATE_PROJECT,
  DUPLICATE_PROJECT
} from "shared/types/ProjectMutations"
import {
  DuplicateEntityInput,
  Process
} from "shared/types/__generated__/graphql"
import { DuplicateEntityInputSchema } from "shared/types/__generated__/validation"

import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import {
  AllProjectPermissionsDocument,
  useProjectChildrenLazyQuery,
  useProjectLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { createProjectInCache } from "entities/projects/lib/createProjectInCache"
import { evictProjectAndSort } from "entities/projects/lib/evictProjectAndSort"
import { readProjectFragment } from "entities/projects/lib/readProjectFragment"
import { useProjectsActions } from "entities/projects/model/store/useProjectsActions"
import z from "zod"

const DuplicateEntityVariablesSchema = z.object({
  input: DuplicateEntityInputSchema()
})

export function useDuplicateProject() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const { setProjectPending, unsetProjectPending } = useProjectsActions()
  const startProjectRequest = useProjectRequestMonitor()
  const [fetchProjects] = useProjectsLazyQuery()
  const [fetchProjectForRefetch] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Project.DUPLICATE_PROJECT.Start"),
      success: t("Request.Project.DUPLICATE_PROJECT.Success"),
      failure: t("Request.Project.DUPLICATE_PROJECT.Failure")
    }
  })

  const handleError = useEvent(
    (
      error: ApolloError,
      options:
        | BaseMutationOptions<
            DuplicateEntityMutation,
            Partial<DuplicateEntityMutationVariables>
          >
        | undefined
    ) => {
      if (options?.variables?.input?.desiredId) {
        evictProjectAndSort(options?.variables?.input?.desiredId, apolloCache)
      }
    }
  )

  const [apolloMutate, rest] = useApolloMutation<
    DuplicateEntityMutation,
    DuplicateEntityMutationVariables
  >(DuplicateEntityDocument)

  const [fetchProject] = useProjectLazyQuery()
  const [fetchProjectChildren] = useProjectChildrenLazyQuery()
  const handleProcess = useProcess()

  const handleProcessComplete = useEvent((process: Process) => {
    if (process.copyId) {
      fetchProjectChildren({
        variables: { id: process.copyId },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
      return fetchProject({
        variables: { id: process.copyId },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
    }
    throw new Error("Process contained no ID for Result!")
  })

  const duplicateProject = useEvent(
    async ({
      projectID,
      shareID,
      title,
      onError
    }: {
      projectID: string
      title?: string
      shareID?: string
      onError?: (
        error: ApolloError,
        options: BaseMutationOptions | undefined
      ) => void
    }) => {
      const project = readProjectFragment(projectID, apolloCache)
      if (!project) {
        throw new Error("Project not found!")
      }
      const id = generateUUID()
      const newTitle = title || project.title

      const now = new Date().toISOString()

      createProjectInCache({
        cache: apolloCache,
        project: {
          ...project,
          title: newTitle,
          position: 0,
          createdAt: now,
          updatedAt: now,
          lastChanged: now,
          team: null,
          id,
          hasClarificationRequest: false
        },
        writeChildrenQuery: false
      })

      const input: DuplicateEntityInput = {
        id: generateUUID(),
        recursive: true,
        label: newTitle,
        desiredId: id,
        source: {
          id: shareID || projectID,
          type: shareID ? ShareLinkTypename : ProjectTypename
        }
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DUPLICATE_PROJECT
      })

      emitAppEvent(CREATE_PROJECT, { id })
      setProjectPending({ projectID: id })

      try {
        const mutationResult = await callMutation(apolloMutate, {
          variables: { input },
          transform: data => data.process,
          schemas: {
            input: DuplicateEntityVariablesSchema,
            response: MutationSchema.extend({ process: z.custom<Process>() })
          },
          apolloOptions: {
            refetchQueries: [{ query: AllProjectPermissionsDocument }],
            onError: (error, options) => {
              onError?.(error, options)
              handleError(error, options)
            }
          }
        })

        const process = mutationResult.data
        if (!process) {
          throw new Error("Duplication returned no Process!")
        }

        const processResult = await handleProcess({
          process,
          onComplete: handleProcessComplete
        })

        raiseSuccessToast()
        return processResult.data?.project
      } catch (e) {
        evictProjectAndSort(id, apolloCache)
        evictProjectPrice(projectID, apolloCache, true)
        fetchProjects()
        raiseErrorToast()
        throw e
      } finally {
        completeProjectRequest()
        unsetProjectPending({ projectID: id })
        fetchProjectForRefetch({ variables: { id: projectID } })
        evictProjectHistory(projectID, apolloCache)
      }
    }
  )
  return {
    duplicateProject,
    ...rest
  }
}
